import { SubscriptionService } from '../../api/services/SubscriptionService';
import { PaymentMethodService } from '../../api/services/PaymentMethodService';
import { ApiError } from '../../api/core/ApiError';

import type { Routemize_DTOs_Billing_TenantSubscriptionDto } from '../../api/models/Routemize_DTOs_Billing_TenantSubscriptionDto';
import type { Routemize_DTOs_Billing_PaymentMethodDto } from '../../api/models/Routemize_DTOs_Billing_PaymentMethodDto';
import type { Routemize_DTOs_Billing_SubscriptionUsageDto } from '../../api/models/Routemize_DTOs_Billing_SubscriptionUsageDto';
import type { Routemize_DTOs_Billing_CreateSubscriptionDto } from '../../api/models/Routemize_DTOs_Billing_CreateSubscriptionDto';
import type { Routemize_DTOs_Billing_UpdateSubscriptionDto } from '../../api/models/Routemize_DTOs_Billing_UpdateSubscriptionDto';
import type { Routemize_DTOs_Billing_CancelSubscriptionDto } from '../../api/models/Routemize_DTOs_Billing_CancelSubscriptionDto';
import type { Routemize_DTOs_Billing_DowngradeValidationResultDto } from '../../api/models/Routemize_DTOs_Billing_DowngradeValidationResultDto';
import type { Routemize_DTOs_Billing_UserRemovalResultDto } from '../../api/models/Routemize_DTOs_Billing_UserRemovalResultDto';
import type { Routemize_Constants_SubscriptionTier } from '../../api/models/Routemize_Constants_SubscriptionTier';

// Business interfaces for enhanced data views
export interface SubscriptionSummary {
  subscription: Routemize_DTOs_Billing_TenantSubscriptionDto | null;
  usage: Routemize_DTOs_Billing_SubscriptionUsageDto | null;
  isActive: boolean;
  isInTrial: boolean;
  daysUntilRenewal: number | null;
  noSubscriptionExists: boolean;
}

export interface BillingDashboardData {
  subscription: SubscriptionSummary;
}

/**
 * Business logic manager for billing and subscription management
 * Handles subscriptions, payment methods, and usage tracking
 */
export class BillingManager {

  // Session-lifetime in-memory caches. The singleton lives for the whole SPA session, so once
  // subscription/payment info is fetched it is not fetched again until a hard refresh clears the
  // singleton — or a mutation explicitly invalidates it. No time-based expiry.
  private _summaryCache: SubscriptionSummary | null = null;
  private _summaryPromise: Promise<SubscriptionSummary> | null = null;

  // Chargebee payment-methods lookup, shared between the app-wide trial banner and the billing
  // page. Invalidated whenever a card is added or removed.
  private _paymentMethodsCache: Routemize_DTOs_Billing_PaymentMethodDto[] | null = null;
  private _paymentMethodsPromise: Promise<Routemize_DTOs_Billing_PaymentMethodDto[]> | null = null;

  // ============================================================================
  // Subscription Management
  // ============================================================================

  /**
   * Get the current tenant's subscription details
   */
  async getCurrentSubscription(): Promise<Routemize_DTOs_Billing_TenantSubscriptionDto> {
    return await SubscriptionService.getApiAppSubscriptionGetCurrentSubscription();
  }

  /**
   * Get subscription usage metrics
   */
  async getSubscriptionUsage(): Promise<Routemize_DTOs_Billing_SubscriptionUsageDto> {
    return await SubscriptionService.getApiAppSubscriptionGetUsage();
  }

  /**
   * Get comprehensive subscription summary with calculated fields.
   * Results are cached for 60s to avoid duplicate API calls when multiple
   * components (AppInitializer, Sidebar) request the summary on the same page load.
   */
  async getSubscriptionSummary(): Promise<SubscriptionSummary> {
    // Cached for the session — fetched once, reused until a hard refresh or explicit invalidation.
    if (this._summaryCache) {
      return this._summaryCache;
    }

    // Deduplicate concurrent calls — return the same in-flight promise
    if (this._summaryPromise) {
      return this._summaryPromise;
    }

    this._summaryPromise = this._fetchSubscriptionSummary();
    try {
      const result = await this._summaryPromise;
      this._summaryCache = result;
      return result;
    } finally {
      this._summaryPromise = null;
    }
  }

  /** Invalidate the subscription summary cache (e.g. after plan change). Dispatches a
   *  `routemize:plan-invalidated` event so any context listening (e.g. `PlanProvider`)
   *  can refetch and re-sync UI tier gates. */
  invalidateSubscriptionCache(): void {
    this._summaryCache = null;
    this._paymentMethodsCache = null;
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      try {
        window.dispatchEvent(new Event('routemize:plan-invalidated'));
      } catch {}
    }
  }

  /** Clear the sessionStorage gate cache so AppInitializer re-checks on next render */
  clearSubscriptionGateCache(): void {
    try {
      const keys = Object.keys(sessionStorage);
      keys.filter(k => k.startsWith('gate:subscription:')).forEach(k => sessionStorage.removeItem(k));
    } catch {}
  }

  private async _fetchSubscriptionSummary(): Promise<SubscriptionSummary> {
    try {
      const [subscription, usage] = await Promise.all([
        this.getCurrentSubscription(),
        this.getSubscriptionUsage()
      ]);

      const now = Date.now();
      const isActive = subscription?.status === 'Active' || subscription?.status === 'InTrial';
      const isInTrial = subscription?.status === 'InTrial';

      let daysUntilRenewal: number | null = null;
      if (subscription?.nextBillingDate) {
        const nextBillingMs = subscription.nextBillingDate;
        const diffMs = nextBillingMs - now;
        daysUntilRenewal = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      }

      return {
        subscription,
        usage,
        isActive,
        isInTrial,
        daysUntilRenewal,
        noSubscriptionExists: false
      };
    } catch (error) {
      if (this.isSubscriptionNotFoundError(error)) {
        return {
          subscription: null,
          usage: null,
          isActive: false,
          isInTrial: false,
          daysUntilRenewal: null,
          noSubscriptionExists: true,
        };
      }
      throw error;
    }
  }

  private isSubscriptionNotFoundError(error: unknown): boolean {
    if (error instanceof ApiError) {
      if (error.status === 404) return true;
      const errorCode = error.body?.error?.code;
      if (errorCode === 'Subscription:NotFound') return true;
    }
    return false;
  }

  /**
   * Create a new subscription
   */
  async createSubscription(
    dto: Routemize_DTOs_Billing_CreateSubscriptionDto
  ): Promise<Routemize_DTOs_Billing_TenantSubscriptionDto> {
    return await SubscriptionService.postApiAppSubscriptionCreateSubscription(dto);
  }

  /**
   * Change subscription tier/plan
   */
  async changeSubscriptionTier(
    dto: Routemize_DTOs_Billing_UpdateSubscriptionDto
  ): Promise<Routemize_DTOs_Billing_TenantSubscriptionDto> {
    return await SubscriptionService.postApiAppSubscriptionChangeSubscriptionTier(dto);
  }

  /**
   * Cancel the current subscription. Invalidates the cached summary so the page re-fetches
   * the scheduled-to-cancel state instead of the stale pre-cancel one.
   */
  async cancelSubscription(
    dto: Routemize_DTOs_Billing_CancelSubscriptionDto
  ): Promise<void> {
    await SubscriptionService.postApiAppSubscriptionCancelSubscription(dto);
    this.invalidateSubscriptionCache();
    this.clearSubscriptionGateCache();
  }

  /**
   * Reactivate a cancelled subscription
   */
  async reactivateSubscription(): Promise<Routemize_DTOs_Billing_TenantSubscriptionDto> {
    const result = await SubscriptionService.postApiAppSubscriptionReactivateSubscription();
    this.invalidateSubscriptionCache();
    this.clearSubscriptionGateCache();
    return result;
  }

  /**
   * Undo a scheduled cancellation (Chargebee non_renewing). No charge, current term preserved —
   * distinct from reactivateSubscription, which reactivates a genuinely cancelled subscription and
   * bills a brand-new term immediately.
   */
  async keepSubscription(): Promise<Routemize_DTOs_Billing_TenantSubscriptionDto> {
    const result = await SubscriptionService.postApiAppSubscriptionKeepSubscription();
    this.invalidateSubscriptionCache();
    this.clearSubscriptionGateCache();
    return result;
  }

  /**
   * Cancel a pending end-of-term tier change and stay on the current plan.
   */
  async cancelScheduledChange(): Promise<Routemize_DTOs_Billing_TenantSubscriptionDto> {
    const result = await SubscriptionService.postApiAppSubscriptionCancelScheduledChange();
    this.invalidateSubscriptionCache();
    this.clearSubscriptionGateCache();
    return result;
  }

  /**
   * Check if a feature limit has been reached
   */
  async checkFeatureLimit(featureName: string, requestedAmount: number = 1): Promise<boolean> {
    return await SubscriptionService.postApiAppSubscriptionCheckLimit(featureName, requestedAmount);
  }

  /**
   * Pre-flight check: validate whether a tier change is allowed
   */
  async validateTierChange(targetTier: Routemize_Constants_SubscriptionTier): Promise<Routemize_DTOs_Billing_DowngradeValidationResultDto> {
    return await SubscriptionService.postApiAppSubscriptionValidateChange(targetTier);
  }

  /**
   * Purchase an additional user seat (Scale plan only)
   */
  async addUserSeat(): Promise<Routemize_DTOs_Billing_TenantSubscriptionDto> {
    return await SubscriptionService.postApiAppSubscriptionAddUserSeat();
  }

  /**
   * Remove an additional user seat (Scale plan only)
   */
  async removeUserSeat(): Promise<Routemize_DTOs_Billing_TenantSubscriptionDto> {
    return await SubscriptionService.deleteApiAppSubscriptionRemoveUserSeat();
  }

  /**
   * Get information about unused add-on seats after user removal
   */
  async getUnusedSeatsInfo(): Promise<Routemize_DTOs_Billing_UserRemovalResultDto> {
    return await SubscriptionService.getApiAppSubscriptionGetUnusedSeatsInfo();
  }

  // ============================================================================
  // Dashboard Data
  // ============================================================================

  /**
   * Get all billing dashboard data in a single call
   * Optimized for dashboard loading
   */
  async getDashboardData(): Promise<BillingDashboardData> {
    const subscription = await this.getSubscriptionSummary();

    return {
      subscription
    };
  }

  // ============================================================================
  // Payment Method Management (Chargebee Proxy)
  // ============================================================================

  /**
   * Get all payment methods from Chargebee for the current tenant
   * Pure proxy - no database storage
   */
  async getPaymentMethods(): Promise<Routemize_DTOs_Billing_PaymentMethodDto[]> {
    // Cached for the session — fetched once, reused until a hard refresh or a card add/remove.
    if (this._paymentMethodsCache) {
      return this._paymentMethodsCache;
    }
    if (this._paymentMethodsPromise) {
      return this._paymentMethodsPromise;
    }
    this._paymentMethodsPromise = PaymentMethodService.getApiAppPaymentMethodGetPaymentMethods();
    try {
      const result = await this._paymentMethodsPromise;
      this._paymentMethodsCache = result;
      return result;
    } finally {
      this._paymentMethodsPromise = null;
    }
  }

  /** Drop the cached payment-methods list so the next getPaymentMethods() re-queries Chargebee.
   *  Dispatches `routemize:payment-methods-changed` so persistent "add a card" affordances
   *  can re-check and disappear as soon as a card is on file. */
  invalidatePaymentMethodsCache(): void {
    this._paymentMethodsCache = null;
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      try {
        window.dispatchEvent(new Event('routemize:payment-methods-changed'));
      } catch {
        // Notifying listeners is best-effort; the cache is already cleared.
      }
    }
  }

  /**
   * Delete a payment method from Chargebee
   * @param paymentMethodId - Chargebee payment source ID
   */
  async deletePaymentMethod(paymentMethodId: string): Promise<void> {
    await PaymentMethodService.deleteApiAppPaymentMethodDeletePaymentMethod(paymentMethodId);
    this.invalidatePaymentMethodsCache();
  }

  /**
   * Add a new payment method using Chargebee token
   * @param token - Chargebee card token from tokenization
   */
  async addPaymentMethod(token: string): Promise<void> {
    await PaymentMethodService.putApiAppPaymentMethodUpdatePaymentMethod({
      paymentMethodToken: token,
      setAsPrimary: true,
      collectDues: true
    });
    this.invalidatePaymentMethodsCache();
  }

  /**
   * Set a payment method as the default
   * Note: Currently sets default through Chargebee customer update
   * @param paymentMethodId - Chargebee payment source ID
   */
  async setDefaultPaymentMethod(paymentMethodId: string): Promise<void> {
    // For now, log that this needs backend implementation
    console.log('setDefaultPaymentMethod called with:', paymentMethodId);
    // The backend needs to implement this endpoint
    // For now, we'll just refresh the payment methods which may trigger Chargebee's default logic
    await this.getPaymentMethods();
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  /**
   * Format currency values
   */
  formatCurrency(amount: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(amount);
  }

  /**
   * Format date values
   */
  formatDate(value: number | string, timezone?: string): string {
    // Accepts both epoch millis and the ISO date strings the generated DTOs use for
    // nextBillingDate / currentPeriodEnd / scheduledChangeEffectiveDate.
    return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      ...(timezone ? { timeZone: timezone } : {})
    });
  }

  /**
   * Get status display text
   */
  getStatusDisplay(status: string): { text: string; color: string } {
    const statusMap: Record<string, { text: string; color: string }> = {
      'Active': { text: 'Active', color: 'green' },
      'InTrial': { text: 'In Trial', color: 'blue' },
      'Cancelled': { text: 'Cancelled', color: 'red' },
      'Expired': { text: 'Expired', color: 'gray' },
      'PastDue': { text: 'Past Due', color: 'orange' }
    };

    return statusMap[status] || { text: status, color: 'gray' };
  }
}

// Export singleton instance
export const billingManager = new BillingManager();
